import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import {
  VerificationMode,
  type PushNotificationMetadata,
} from '@/api/generated';
import notificationPollingSvg from '@/assets/notification-polling.svg';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { Page } from '@/components/ui/page';
import { useLanguage } from '@/contexts/language-context';
import { useResendCountdown } from '@/hooks/use-resend-countdown';
import { VisualMatchImage } from './visual-match-image';

interface PushNotificationPollingPendingStateProps {
  resend: () => void;
  isResending?: boolean;
  metadata?: PushNotificationMetadata;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const PushNotificationPollingPendingState: React.FC<
  PushNotificationPollingPendingStateProps
> = ({
  resend,
  isResending,
  metadata,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();
  const { canResend, isExhausted, formattedTime } = useResendCountdown(
    metadata?.next_resend_at
  );

  // Check if visual match mode
  const isVisualMatch =
    metadata?.verification_mode === VerificationMode.VISUAL_MATCH;
  const correctImageUrl = metadata?.correct_image_url;

  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='h-full flex flex-col justify-between'>
          <div className='h-full flex flex-col justify-between'>
            <div className='flex flex-col gap-1 relative'>
              {!isVisualMatch && (
                <img
                  src={notificationPollingSvg}
                  alt='Notification polling'
                  className='absolute top-2 right-4 w-30 hidden xs:block'
                />
              )}
              <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
                {t('mfa.checkYourSparkApp')}
              </Text>
              <Text
                weight={FontWeights.REGULAR}
                className='whitespace-break-spaces'
              >
                {isVisualMatch
                  ? t('mfa.visualMatchMessage')
                  : t('mfa.notificationSentMessage')}
              </Text>
              {isVisualMatch && correctImageUrl && (
                <VisualMatchImage imageUrl={correctImageUrl} />
              )}
            </div>
            <div className='flex flex-col gap-1 mt-8'>
              <Text as='p' weight={FontWeights.MEDIUM}>
                {t('mfa.didntReceiveIt')}
              </Text>
              {canResend ? (
                <LoginButton
                  type='button'
                  variant={ButtonVariants.LINK_PRIMARY}
                  onClick={resend}
                  disabled={isResending}
                >
                  {t('mfa.resendNotification')}
                </LoginButton>
              ) : (
                <Text as='p' className='text-secondary'>
                  {isExhausted
                    ? t('mfa.resendLimitReached')
                    : `${t('mfa.requestNewCodeIn')} ${formattedTime}`}
                </Text>
              )}
            </div>
          </div>
        </div>
      </LoginCard>
    </Page>
  );
};
